// IndexedDB wrapper for encrypted storage
import { encryptData, decryptData } from './crypto';

const DB_NAME = 'AravelaDB';
const DB_VERSION = 1;

// Store names
export const STORES = {
  AUTH: 'auth',
  DIARY: 'diary',
  SESSIONS: 'sessions', // Chat sessions with metadata
  PATTERNS: 'patterns',
  INTERACTIONS: 'interactions',
  CONTACTS: 'contacts',
  FOLLOW_UPS: 'follow_ups',
  TOOL_OUTCOMES: 'tool_outcomes',
  EPISODE_SUMMARIES: 'episode_summaries',
  APP_STATE: 'app_state', // For onboarding, version tracking
};

let dbInstance = null;

// Initialize database
export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object stores
      if (!db.objectStoreNames.contains(STORES.AUTH)) {
        db.createObjectStore(STORES.AUTH, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.DIARY)) {
        const diaryStore = db.createObjectStore(STORES.DIARY, { keyPath: 'id', autoIncrement: true });
        diaryStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
        const sessionStore = db.createObjectStore(STORES.SESSIONS, { keyPath: 'id' });
        sessionStore.createIndex('startTime', 'startTime', { unique: false });
        sessionStore.createIndex('endTime', 'endTime', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.PATTERNS)) {
        db.createObjectStore(STORES.PATTERNS, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.INTERACTIONS)) {
        const interactionStore = db.createObjectStore(STORES.INTERACTIONS, { keyPath: 'id', autoIncrement: true });
        interactionStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.CONTACTS)) {
        db.createObjectStore(STORES.CONTACTS, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.FOLLOW_UPS)) {
        const followUpStore = db.createObjectStore(STORES.FOLLOW_UPS, { keyPath: 'id', autoIncrement: true });
        followUpStore.createIndex('timestamp', 'timestamp', { unique: false });
        followUpStore.createIndex('status', 'status', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.TOOL_OUTCOMES)) {
        db.createObjectStore(STORES.TOOL_OUTCOMES, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.EPISODE_SUMMARIES)) {
        const episodeStore = db.createObjectStore(STORES.EPISODE_SUMMARIES, { keyPath: 'id', autoIncrement: true });
        episodeStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.APP_STATE)) {
        db.createObjectStore(STORES.APP_STATE, { keyPath: 'key' });
      }
    };
  });
};

// Get database instance
const getDB = async () => {
  if (!dbInstance) {
    await initDB();
  }
  return dbInstance;
};

// Save encrypted data
export const saveEncrypted = async (storeName, data, key) => {
  const db = await getDB();
  const encrypted = await encryptData(data, key);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put({ ...encrypted, id: data.id || undefined });

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get encrypted data
export const getEncrypted = async (storeName, id, key) => {
  const db = await getDB();
  
  return new Promise(async (resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = async () => {
      if (request.result) {
        try {
          const decrypted = await decryptData(request.result, key);
          resolve(decrypted);
        } catch (error) {
          reject(new Error('Decryption failed - wrong passcode?'));
        }
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Get all encrypted data from store
export const getAllEncrypted = async (storeName, key) => {
  const db = await getDB();
  
  return new Promise(async (resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = async () => {
      try {
        const decryptedItems = await Promise.all(
          request.result.map(item => decryptData(item, key))
        );
        resolve(decryptedItems);
      } catch (error) {
        reject(new Error('Decryption failed'));
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Delete data
export const deleteData = async (storeName, id) => {
  const db = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Clear all data from store
export const clearStore = async (storeName) => {
  const db = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// App state helpers (for onboarding, version tracking - NOT encrypted)
export const getAppState = async (key) => {
  const db = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.APP_STATE], 'readonly');
    const store = transaction.objectStore(STORES.APP_STATE);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result?.value);
    request.onerror = () => reject(request.error);
  });
};

export const setAppState = async (key, value) => {
  const db = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.APP_STATE], 'readwrite');
    const store = transaction.objectStore(STORES.APP_STATE);
    const request = store.put({ key, value });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};